import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';

import { useStores } from '@/hooks/use-stores';
import { EngineStepsEnum, EngineSubstepsEnum } from '@/store/Engine/constants';
import DefaultSetpoints from '@/comp/pages/EnginePage/Engine/DefaultRule/DefaultSetpoints';
import { ValidationMessagePopoverGlobalStyle } from '@/comp/pages/EnginePage/Engine/Common/styled';
import { EPButton } from '@/components';

import { RulesGeneratorModalContainer } from './styled';
import { sortSetpointsByElementIndex, validateNewFallbackScenarioSetpoints } from '@/store/Engine/utils';
import { EngineStore } from '@/store/Engine/EngineStore';
import StationStore from '@/store/Station/StationStore';
import DevicesStore from '@/store/Devices/DevicesStore';
import { message } from 'antd';

const RulesGeneratorModal = observer(
  ({
    rulesObject,
    onOk,
    disabledCompressors,
    hasTriedToSubmit,
    setHasTriedToSubmit,
    type,
    isNewFallbackScenario,
  }: {
    rulesObject;
    onOk;
    disabledCompressors?;
    hasTriedToSubmit?;
    setHasTriedToSubmit?;
    type?;
    isNewFallbackScenario?;
  }) => {
    const { t } = useTranslation();
    const {
      engineStore,
      stationStore,
      devicesStore,
    }: { engineStore?: EngineStore; stationStore?: StationStore; devicesStore?: DevicesStore } = useStores();

    useEffect(() => {
      if (!rulesObject?.rules?.length)
        rulesObject?.setField(
          'rules',
          engineStore.defaultSetpointsConfiguration?.setpoints?.map(setpoint => ({
            ...setpoint,
            name: `Table${engineStore.fallbackTableNameCounter + 1}`,
          })),
        );
    }, []);

    const onSaveClick = e => {
      setHasTriedToSubmit(true);
      engineStore.clearValidationMessages();
      if (isNewFallbackScenario) {
        // Validate setpoints specifically for new fallback scenario, only checking editable setpoints
        if (rulesObject?.rules?.length) {
          const validationMessages = validateNewFallbackScenarioSetpoints(
            rulesObject.rules,
            disabledCompressors,
            devicesStore?.devicesMetadata,
          );
          engineStore.validationMessages = validationMessages;
        }
      } else {
        engineStore.validate(
          type ? EngineStepsEnum.SensorsConfiguration : EngineSubstepsEnum.FallbackScenarios,
          type ? `${type}SensorsPriority` : null,
          null,
          stationStore.currentStation.devices,
        );
      }

      if (engineStore.validationMessages?.length) {
        message.error(t(engineStore.validationMessages?.[0]?.message));
        return;
      }

      if (engineStore.validationMessages?.length === 0 && rulesObject?.rules?.[0]?.name?.length) {
        onOk(e);
        setHasTriedToSubmit(false);
      }
    };

    return (
      <RulesGeneratorModalContainer>
        <div className="table-container">
          <span className="rule-name">{rulesObject?.rules?.[0]?.name}</span>
          <DefaultSetpoints
            className="default-setpoints"
            isEditMode
            from="rulesGeneratorModal"
            setpoints={sortSetpointsByElementIndex(rulesObject.rules, devicesStore?.sortedCompressors)}
            rulesObject={rulesObject}
            disabledCompressors={disabledCompressors}
            hasTriedToSubmit={hasTriedToSubmit}
            type={type}
            isNewFallbackScenario={isNewFallbackScenario}
          />
        </div>
        <EPButton type="primary" onClick={onSaveClick} className="ok-button">
          <span className="button-label">{t('ok')}</span>
        </EPButton>
        <ValidationMessagePopoverGlobalStyle />
      </RulesGeneratorModalContainer>
    );
  },
);

export default RulesGeneratorModal;
